import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useProducts, fromApi } from "@/lib/products";

const KEY = "shopabee-basket-v1";
const SEEN_KEY = "shopabee-basket-seen-v1";

const read = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY));
    if (!v || typeof v !== "object" || Array.isArray(v)) return {};
    return Object.fromEntries(Object.entries(v).filter(([, q]) => Number.isInteger(q) && q > 0));
  } catch {
    return {};
  }
};

const BasketContext = createContext(null);
export const useBasket = () => useContext(BasketContext);

export const BasketProvider = ({ children }) => {
  const { products, loading, refresh } = useProducts();
  const [items, setItems] = useState(read);
  const ref = useRef(items);
  ref.current = items;

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  // Last-seen title/photo for display only, so unavailable lines stay recognisable
  const [seen, setSeen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SEEN_KEY)) || {}; } catch { return {}; }
  });
  useEffect(() => {
    const next = Object.fromEntries(Object.keys(items).map((id) => [id, byId[id] ? { name: byId[id].name, img: byId[id].img } : seen[id]]).filter(([, v]) => v));
    if (JSON.stringify(next) !== JSON.stringify(seen)) {
      setSeen(next);
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
    }
  }, [items, byId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clamp stored quantities to current stock whenever product data is (re)loaded
  useEffect(() => {
    if (loading) return;
    const next = { ...ref.current };
    let changed = false;
    Object.entries(next).forEach(([id, q]) => {
      const p = byId[id];
      if (p && p.qty > 0 && q > p.qty) {
        next[id] = p.qty;
        changed = true;
        toast(`Sorry, only ${p.qty} left in stock.`, { description: `We've updated the quantity of ${p.name}.` });
      }
    });
    if (changed) setItems(next);
  }, [byId, loading]);

  const current = async (p) => {
    if (p.demo) return p;
    try {
      const fresh = fromApi((await api.get(`/products/${p.id}`)).data);
      if (fresh.qty !== byId[p.id]?.qty || fresh.price !== byId[p.id]?.price) refresh();
      return fresh;
    } catch {
      refresh();
      return null;
    }
  };

  const add = async (p, step = 1) => {
    const fresh = await current(p);
    if (!fresh || fresh.qty === 0) return toast("This item is currently unavailable.");
    const inBasket = ref.current[p.id] || 0;
    if (inBasket + step > fresh.qty) return toast(`Sorry, only ${fresh.qty} left in stock.`, { description: fresh.name });
    setItems({ ...ref.current, [p.id]: inBasket + step });
    toast("Added to basket", { description: fresh.name });
  };

  const increase = async (id) => {
    const p = byId[id];
    const fresh = p && (await current(p));
    if (!fresh || fresh.qty === 0) return toast("This item is currently unavailable.");
    const q = ref.current[id] || 0;
    if (q >= fresh.qty) return q > fresh.qty ? undefined : toast(`Sorry, only ${fresh.qty} left in stock.`, { description: fresh.name });
    setItems({ ...ref.current, [id]: q + 1 });
  };

  const decrease = (id) => {
    const q = ref.current[id] || 0;
    if (q > 1) setItems({ ...ref.current, [id]: q - 1 });
  };

  const removeMany = (ids) => {
    const next = { ...ref.current };
    ids.forEach((id) => delete next[id]);
    setItems(next);
  };

  const remove = (id) => {
    const next = { ...ref.current };
    delete next[id];
    setItems(next);
  };

  // Reminder-email link: /basket?restore=id:qty,id:qty (quantities are clamped to stock above)
  const restore = (pairs) => {
    const next = { ...ref.current };
    pairs.forEach(([id, q]) => { if (/^[\w-]{1,64}$/.test(id) && Number.isInteger(q) && q > 0 && q <= 99) next[id] = Math.max(next[id] || 0, q); });
    setItems(next);
  };

  const lines = Object.entries(items).map(([id, q]) => {
    const p = byId[id];
    const available = !!p && p.qty > 0;
    return { id, q, p, available, pending: loading && !p, seen: seen[id], total: available ? p.price * q : 0 };
  });
  const ready = lines.filter((l) => l.available);
  const count = ready.reduce((a, l) => a + l.q, 0);
  const subtotal = ready.reduce((a, l) => a + l.total, 0);

  return (
    <BasketContext.Provider value={{ lines, count, subtotal, loading, add, increase, decrease, remove, removeMany, refresh, restore }}>
      {children}
    </BasketContext.Provider>
  );
};
